import { GoogleGenAI, Type } from "@google/genai";
import { db, auth } from '../firebase';
import {
  collection, doc, addDoc, setDoc, getDoc, updateDoc,
  onSnapshot, query, orderBy, limit, where,
  serverTimestamp, Timestamp, increment
} from 'firebase/firestore';
import { Incident, ThreatLevel, GameRound, GameAnswer, PlayerStats, GameRole, ACHIEVEMENTS } from '../types';

const ROUND_DURATION_MS = 12000; // 12 seconds to answer

// ── Question generation ───────────────────────────────────────────────────────

function detectRole(incident: Incident): GameRole | 'ALL' {
  const t = incident.type.toLowerCase() + ' ' + incident.description.toLowerCase();
  if (/shoot|gun|weapon|assault|fight|pursuit|traffic stop|arrest|robbery|burgl|theft|suspect|warrant/i.test(t)) return 'OFFICER';
  if (/medic|cardiac|overdose|trauma|injury|unconscious|breathing|chest pain|stroke|seizure|unresponsive|ambulance/i.test(t)) return 'PARAMEDIC';
  if (/fire|smoke|explosion|hazmat|chemical|gas leak|structure|wildfire|arson/i.test(t)) return 'FIREFIGHTER';
  return 'ALL';
}

export async function generateGameQuestion(
  incident: Incident,
  apiKey?: string
): Promise<Omit<GameRound, 'id' | 'startTime' | 'endTime' | 'status'> | null> {
  const ai = new GoogleGenAI({ apiKey: apiKey || process.env.GEMINI_API_KEY || '' });
  const targetRole = detectRole(incident);
  const jackpot = incident.threatLevel === ThreatLevel.CRITICAL;

  const roleContext: Record<string, string> = {
    OFFICER:     'a law enforcement officer responding to this call. Test knowledge of police procedure, use-of-force thresholds, Miranda rights, probable cause, radio codes, or tactical decision-making.',
    PARAMEDIC:   'a paramedic/EMT responding to this emergency. Test knowledge of triage priorities, vital signs assessment, drug interactions, airway management, CPR protocols, or trauma treatment.',
    FIREFIGHTER: 'a firefighter/rescue specialist at this scene. Test knowledge of fire behavior, hazmat identification, IDLH limits, ladder placement, rescue priority, or ICS command structure.',
    ALL:         'a community safety observer monitoring this incident. Test general public safety awareness, situational awareness, emergency response knowledge, or civic preparedness.',
  };

  const roleKey = targetRole === 'ALL' ? 'ALL' : targetRole;
  const context = roleContext[roleKey];

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: `You are generating a training scenario question for ${context}

Active Incident:
Type: ${incident.type}
Threat Level: ${incident.threatLevel}
Description: ${incident.description}
Location: ${incident.location.lat.toFixed(4)}, ${incident.location.lng.toFixed(4)}
${incident.freshnessMinutes ? `Reported: ${incident.freshnessMinutes} minutes ago` : ''}

Create ONE realistic, educational multiple-choice question directly relevant to this specific incident.
The question must have exactly 4 choices, only ONE is correct.
The wrong answers should be plausible but clearly incorrect to someone with real training.
Keep question under 120 characters. Each choice under 80 characters.
The explanation should be 1-2 sentences of real educational value.`,
      config: {
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            question:     { type: Type.STRING },
            choices:      { type: Type.ARRAY, items: { type: Type.STRING }, minItems: 4, maxItems: 4 },
            correctIndex: { type: Type.INTEGER },
            explanation:  { type: Type.STRING },
          },
          required: ['question', 'choices', 'correctIndex', 'explanation']
        },
        temperature: 0.7,
      }
    });

    const data = JSON.parse(response.text || '{}');
    if (!data.question || !data.choices || data.choices.length !== 4) return null;

    return {
      incidentId:          incident.id,
      incidentType:        incident.type,
      incidentDescription: incident.description.slice(0, 200),
      incidentLocation:    incident.location,
      targetRole,
      question:            data.question,
      choices:             data.choices as [string, string, string, string],
      correctIndex:        data.correctIndex,
      explanation:         data.explanation,
      jackpot,
      pointsBase:          jackpot ? 300 : (targetRole === 'ALL' ? 75 : 150),
    };
  } catch (e) {
    console.error('[GameService] Question generation failed:', e);
    return null;
  }
}

// ── Firestore round management ────────────────────────────────────────────────

export async function createGameRound(
  incident: Incident,
  apiKey?: string
): Promise<string | null> {
  const questionData = await generateGameQuestion(incident, apiKey);
  if (!questionData) return null;

  const now = Date.now();
  const roundData: Omit<GameRound, 'id'> = {
    ...questionData,
    startTime: now,
    endTime:   now + ROUND_DURATION_MS,
    status:    'ACTIVE',
  };

  try {
    const ref = await addDoc(collection(db, 'game_rounds'), {
      ...roundData,
      createdAt: serverTimestamp(),
    });
    return ref.id;
  } catch (e) {
    console.error('[GameService] Round creation failed:', e);
    return null;
  }
}

export function subscribeToActiveRound(
  callback: (round: GameRound | null) => void
): () => void {
  const q = query(
    collection(db, 'game_rounds'),
    where('status', '==', 'ACTIVE'),
    orderBy('startTime', 'desc'),
    limit(1)
  );

  return onSnapshot(q, (snap) => {
    if (snap.empty) { callback(null); return; }
    const d = snap.docs[0];
    const data = d.data();
    callback({ id: d.id, ...data } as GameRound);
  });
}

export function subscribeToRoundAnswers(
  roundId: string,
  callback: (answers: GameAnswer[]) => void
): () => void {
  const q = query(
    collection(db, 'game_answers'),
    where('roundId', '==', roundId),
    orderBy('timestamp', 'asc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as GameAnswer)));
  });
}

export async function endRound(roundId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'game_rounds', roundId), { status: 'ENDED' });
  } catch (e) {
    console.error('[GameService] End round failed:', e);
  }
}

// ── Answer submission ─────────────────────────────────────────────────────────

export async function submitAnswer(
  round: GameRound,
  choiceIndex: number,
  responseTimeMs: number,
  existingAnswers: GameAnswer[],
  currentStreak: number
): Promise<{ pointsEarned: number; isCorrect: boolean; newAchievements: string[] }> {
  const user = auth.currentUser;
  if (!user) return { pointsEarned: 0, isCorrect: false, newAchievements: [] };

  // Already answered?
  if (existingAnswers.some(a => a.userId === user.uid)) {
    return { pointsEarned: 0, isCorrect: false, newAchievements: [] };
  }

  // Load player stats early — used to determine criminal role and check achievements
  const statsSnap = await getDoc(doc(db, 'player_stats', user.uid)).catch(() => null);
  const stats = statsSnap?.data() as PlayerStats | undefined;

  // Criminals win by picking any answer that ISN'T the correct cop answer
  const isCriminal = (stats?.preferredRole === 'CRIMINAL');
  const isCorrect = isCriminal
    ? choiceIndex !== round.correctIndex   // criminal evades if cop answer is wrong
    : choiceIndex === round.correctIndex;
  const isFirst = existingAnswers.filter(a => a.isCorrect).length === 0;

  // Speed multiplier: max at 0s, 0.1 at 12s
  const speedMult = isCorrect
    ? Math.max(0.1, 1 - (responseTimeMs / ROUND_DURATION_MS) * 0.9)
    : 0;

  // Streak bonus after streak 3+
  const streakBonus = isCorrect && currentStreak >= 3
    ? Math.min((currentStreak - 2) * 25, 200)
    : 0;

  // Jackpot multiplier
  const jackpotMult = round.jackpot ? 3 : 1;

  const pointsEarned = isCorrect
    ? Math.round(round.pointsBase * speedMult * jackpotMult) + streakBonus
    : 0;

  const newAchievements: string[] = [];
  const hour   = new Date().getHours();
  const minute = new Date().getMinutes();

  // Check achievements
  const existing = stats?.achievements || [];

  if (isCorrect && !existing.includes('FIRST_BLOOD')) newAchievements.push('FIRST_BLOOD');
  if (isCorrect && isFirst && !existing.includes('THE_DISPATCH')) newAchievements.push('THE_DISPATCH');
  if (isCorrect && round.jackpot && !existing.includes('JACKPOT')) newAchievements.push('JACKPOT');

  // Criminal-specific achievements
  if (isCriminal) {
    const criminalWins = (stats?.roleScores?.CRIMINAL || 0);
    if (isCorrect && criminalWins + 1 >= 5  && !existing.includes('GHOST_WINS'))    newAchievements.push('GHOST_WINS');
    if (isCorrect && criminalWins + 1 >= 10 && !existing.includes('MOST_WANTED'))   newAchievements.push('MOST_WANTED');
  }

  const newStreak = isCorrect ? currentStreak + 1 : 0;
  if (!isCriminal) {
    if (newStreak >= 3  && !existing.includes('STREAK_3'))  newAchievements.push('STREAK_3');
    if (newStreak >= 5  && !existing.includes('STREAK_5'))  newAchievements.push('STREAK_5');
    if (newStreak >= 10 && !existing.includes('STREAK_10')) newAchievements.push('STREAK_10');
  } else {
    if (newStreak >= 5  && !existing.includes('GHOST_STREAK')) newAchievements.push('GHOST_STREAK');
  }

  if (hour === 4 && minute === 44 && isCorrect && !existing.includes('CODE_444')) newAchievements.push('CODE_444');
  if (hour >= 2 && hour < 5 && !existing.includes('NIGHT_WATCH')) {
    const nightCorrect = (stats?.roleScores as any)?.nightWatch || 0;
    if (nightCorrect >= 4) newAchievements.push('NIGHT_WATCH');
  }
  if ((stats?.correctAnswers || 0) + (isCorrect ? 1 : 0) >= 100 && !existing.includes('CENTURY')) newAchievements.push('CENTURY');

  // Write answer
  await addDoc(collection(db, 'game_answers'), {
    roundId:       round.id,
    userId:        user.uid,
    displayName:   user.displayName || 'Ghost',
    photoURL:      user.photoURL || '',
    choiceIndex,
    isCorrect,
    responseTimeMs,
    pointsEarned,
    isFirst,
    timestamp:     Date.now(),
  } as Omit<GameAnswer, 'id'>);

  // Update player stats
  const roleKey = isCriminal ? 'CRIMINAL' : (round.targetRole === 'ALL' ? 'OBSERVER' : round.targetRole);
  await setDoc(doc(db, 'player_stats', user.uid), {
    displayName:    user.displayName || 'Ghost',
    photoURL:       user.photoURL || '',
    gameScore:      increment(pointsEarned),
    totalAnswers:   increment(1),
    correctAnswers: increment(isCorrect ? 1 : 0),
    currentStreak:  newStreak,
    bestStreak:     Math.max(stats?.bestStreak || 0, newStreak),
    lastActive:     Date.now(),
    ...(newAchievements.length > 0 ? {
      achievements: [...(existing || []), ...newAchievements]
    } : {}),
    [`roleScores.${roleKey}`]: increment(pointsEarned),
  }, { merge: true });

  return { pointsEarned, isCorrect, newAchievements };
}

// ── Leaderboard ───────────────────────────────────────────────────────────────

export function subscribeToGameLeaderboard(
  callback: (players: PlayerStats[]) => void
): () => void {
  const q = query(
    collection(db, 'player_stats'),
    orderBy('gameScore', 'desc'),
    limit(15)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ userId: d.id, ...d.data() } as PlayerStats)));
  });
}

// ── Easter egg: Arya's Watch ──────────────────────────────────────────────────

let lastActivityTime = Date.now();

export function updateActivityTime() {
  lastActivityTime = Date.now();
}

export function getMinutesSinceActivity(): number {
  return (Date.now() - lastActivityTime) / 60000;
}
